import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {v4 as uuidv4} from 'uuid';
import {makeStyles} from 'tss-react/mui';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import {DatePicker} from '@mui/x-date-pickers';

import {GroupBox, SelectDs, SelectPlus, hooks} from 'lib.front';
import {transformacion, validacion, cadenas} from 'lib.common';
import {urlsCrm} from '../../../config/keys';
import {
   ALMACENAMIENTO,
   ANIO,
   CLIENTE,
   EMAIL,
   esquemaCampos,
   esquemaValidacion,
   FECHA_EMISION,
   FECHA_EXPIRACION,
   NRO_DOC,
   NRO_USUARIOS,
   PLAN,
   PLAN_DENOMINACION,
   PRECIO,
   PRODUCTO,
   PRODUCTO_DENOMINACION,
   RAZON_SOCIAL,
   SERIE_DOC,
   TIPO_DOC
} from './esquemas';
import {
   queryData,
   queryGrupoCliente,
   mutationRegistrarSuscripcion,
   M_REGISTRAR_SUSCRIPCION,
   Q_DATA_PRODUCTOS,
   Q_DATA_PLANES,
   Q_MAX_GRUPO_CLIENTE
} from './queries';
import {showError, showSucces, showWarning} from 'src/common/toast';
import {loadingModal, openMsg} from 'src/redux-app';

const {denominacionCliente} = transformacion;
const {useValidacion, useApolloQ, useApolloLazyQ, useApolloMutation} = hooks;

const useStyles = makeStyles()(theme => ({
   container: {
      marginTop: theme.spacing(2)
   },
   tabla: {
      width: '100%'
   }
}));

const tiposDocumento = ['01', '03'];

function genValue(reg) {
   return reg.codigo;
}
function genLabel(reg) {
   return reg.denominacion;
}

function RegistrarSuscripcionNube({usuario}) {
   const dispatch = useDispatch();
   const {classes, cx} = useStyles();
   //Modal de espera
   function openWaitModal(load) {
      dispatch(loadingModal(load));
   }
   //Modal para mensajes
   function openMsgModal(msg) {
      dispatch(openMsg(msg));
   }
   //Estados
   const {
      campos,
      existeError,
      handleOnChange,
      validacionManual,
      setCampo,
      validarTodos,
      inicializar
   } = useValidacion(esquemaCampos, esquemaValidacion);
   function onBlurValidacion(e) {
      validacionManual(e.target.name, e.target.value);
   }

   //Data Generica
   const [productos, setProductos] = useState([]);
   const [planes, setPlanes] = useState([]);
   function procesarDataGenerica(data) {
      let dataProductos = data[Q_DATA_PRODUCTOS];
      if (dataProductos && dataProductos.length > 0) {
         dataProductos = dataProductos.filter(item => item.productoGlobal != null);
         setProductos(dataProductos);
      }
      let dataPlanes = data[Q_DATA_PLANES];
      if (dataPlanes && dataPlanes.length > 0) {
         dataPlanes = dataPlanes.filter(item => item.activo && item.id != 1);
         setPlanes(dataPlanes);
      }
      inicializarDataPlanProducto({productos: dataProductos, planes: dataPlanes});
   }
   useApolloQ(queryData, {
      callbackProcesarData: procesarDataGenerica,
      callbackError: openMsgModal,
      callbackLoading: openWaitModal
   });
   //Datos del cliente
   const [grupo, setGrupo] = useState(0);
   function getClienteCodigo(valor) {
      return valor.split('|')[0];
   }
   function getClienteEmail(valor) {
      if (valor) {
         return valor.split('|')[1];
      }
      return '';
   }
   function procesarGrupo(data) {
      if (data[Q_MAX_GRUPO_CLIENTE] && data[Q_MAX_GRUPO_CLIENTE].length > 0) {
         setGrupo(data[Q_MAX_GRUPO_CLIENTE][0].grupo);
      }
   }
   const [getGrupo] = useApolloLazyQ(queryGrupoCliente, {
      callbackProcesarData: procesarGrupo,
      callbackError: openMsgModal,
      callbackLoading: openWaitModal
   });
   useEffect(() => {
      if (campos[CLIENTE].valor && campos[CLIENTE].valor.value !== '') {
         getGrupo({variables: {codigoCliente: getClienteCodigo(campos[CLIENTE].valor.value)}});
      }
   }, [campos[CLIENTE].valor]);
   useEffect(() => {
      if (campos[CLIENTE].valor && campos[CLIENTE].valor.label) {
         setCampo(RAZON_SOCIAL, campos[CLIENTE].valor.label.split('-')[1].trimStart());
      }
   }, [campos[CLIENTE].valor]);

   //Grabar la suscripcion especificada
   async function procesarGenerarLicenciaData(data) {
      if (data[M_REGISTRAR_SUSCRIPCION] && data[M_REGISTRAR_SUSCRIPCION].msg !== '') {
         showSucces(data[M_REGISTRAR_SUSCRIPCION].msg);
         if (data[M_REGISTRAR_SUSCRIPCION].obs) {
            showWarning(data[M_REGISTRAR_SUSCRIPCION].obs);
         }
         if (data[M_REGISTRAR_SUSCRIPCION].error) {
            showError(data[M_REGISTRAR_SUSCRIPCION].error);
         }
         inicializarFormulario();
      }
   }
   const [callRegistrarSuscripcion] = useApolloMutation(mutationRegistrarSuscripcion, {
      callbackProcesarData: procesarGenerarLicenciaData,
      callbackError: openMsgModal,
      callbackLoading: openWaitModal
   });
   async function procesarRegistrarSuscripcion() {
      const esDataOk = await validarTodos();
      if (esDataOk) {
         let variables = {
            llave: uuidv4(),
            grupo: grupo,
            clienteDenominacion: campos[CLIENTE].valor.label.split('-')[1].trim(),
            anio: campos[ANIO].valor,
            tipoDoc: campos[TIPO_DOC].valor,
            serieDoc: campos[SERIE_DOC].valor,
            nroDoc: campos[NRO_DOC].valor,
            fechaEmision: campos[FECHA_EMISION].valor.toISOString(),
            producto: campos[PRODUCTO].valor,
            productoDenominacion: campos[PRODUCTO_DENOMINACION].valor,
            cliente: campos[CLIENTE].valor.value.split('|')[0].trim(),
            email: campos[EMAIL].valor,
            plan: campos[PLAN].valor,
            planDenominacion: campos[PLAN_DENOMINACION].valor,
            precio: campos[PRECIO].valor,
            nroUsuarios: campos[NRO_USUARIOS].valor,
            almacenamiento: campos[ALMACENAMIENTO].valor,
            fechaVigencia: campos[FECHA_EXPIRACION].valor.toISOString()
         };
         callRegistrarSuscripcion({variables});
      }
   }
   //Estados de control

   //Funciones
   function inicializarDataPlanProducto(data) {
      if (!validacion.esVarNullUndef(data)) {
         let dataProductos = data[Q_DATA_PRODUCTOS];
         if (dataProductos && dataProductos.length > 0) {
            setCampo(PRODUCTO, dataProductos[0].codigo);
            setCampo(PRODUCTO_DENOMINACION, dataProductos[0].denominacion);
         }
         let dataPlanes = data[Q_DATA_PLANES];
         handleOnPlanElegidoChange(-1, dataPlanes);
      }
   }
   function handleOnPlanElegidoChange(idPlanElegido, listaPlanes) {
      let planElegido = {
         id: '',
         denominacion: '',
         nroUsuarios: 0,
         almacenamiento: 0,
         precio: 0
      };
      if (listaPlanes && listaPlanes.length > 0) {
         if (idPlanElegido < 0) {
            if (listaPlanes[0].id != 1) {
               planElegido.id = listaPlanes[0].id;
               planElegido.nroUsuarios = listaPlanes[0].nroUsuarios;
               planElegido.almacenamiento = listaPlanes[0].almacenamiento;
               planElegido.precio = listaPlanes[0].precio;
               planElegido.denominacion = listaPlanes[0].denominacion;
            } else if (listaPlanes.length > 1) {
               planElegido.id = listaPlanes[1].id;
               planElegido.nroUsuarios = listaPlanes[1].nroUsuarios;
               planElegido.almacenamiento = listaPlanes[1].almacenamiento;
               planElegido.precio = listaPlanes[1].precio;
               planElegido.denominacion = listaPlanes[1].denominacion;
            }
         } else {
            planElegido = listaPlanes.find(currPlan => currPlan.id == idPlanElegido) || planElegido;
         }
      }
      setCampo(PLAN, planElegido.id);
      setCampo(PLAN_DENOMINACION, planElegido.denominacion);
      setCampo(NRO_USUARIOS, planElegido.nroUsuarios);
      setCampo(ALMACENAMIENTO, planElegido.almacenamiento);
      setCampo(PRECIO, planElegido.precio);
   }
   function inicializarFormulario() {
      inicializar();
      inicializarDataPlanProducto({productos, planes});
      setGrupo(0);
   }
   const validSuscription =
      campos[CLIENTE].valor &&
      campos[CLIENTE].valor.value &&
      campos[PLAN].valor &&
      campos[TIPO_DOC].valor &&
      campos[SERIE_DOC].valor &&
      campos[NRO_DOC].valor;
   return (
      <Container className={cx('containerbox', classes.container)} maxWidth="md">
         <Grid container spacing={1}>
            <Grid item xs={12}>
               <Typography variant="h4" gutterBottom align="center">
                  Crear Suscripción Nube
               </Typography>
            </Grid>
            <Grid container item xs={12}>
               <GroupBox label="Datos Cliente" fullWidth>
                  <Grid container spacing={1}>
                     <Grid item xs={5}>
                        <SelectPlus
                           url={urlsCrm.graph}
                           name={CLIENTE}
                           label="RUC"
                           entidad="clientes"
                           campos={[
                              'codigo',
                              'nombre',
                              'apPaterno',
                              'apMaterno',
                              'razonSocial',
                              'nroDoc',
                              'email'
                           ]}
                           value={campos[CLIENTE].valor}
                           onChange={elegido => {
                              validacionManual(CLIENTE, elegido);
                              if (elegido && elegido.value) {
                                 const emailRecuperado = cadenas.removeSpaces(
                                    getClienteEmail(elegido.value)
                                 );
                                 validacionManual(EMAIL, emailRecuperado);
                              }
                           }}
                           genId={registro => {
                              return `${registro.nroDoc}|${registro.email}`;
                           }}
                           genLabel={denominacionCliente}
                           error={campos[CLIENTE].error !== ''}
                           helperText={campos[CLIENTE].error}
                           InputLabelProps={{shrink: true}}
                           variant="outlined"
                           margin="dense"
                           fullWidth
                           isClearable
                           excesoElementos
                           recuperarOnFocus
                           required
                           placeholder="Elija un cliente:"
                        />
                     </Grid>
                     <Grid item xs={7}>
                        <TextField
                           label="Razón Social"
                           name={RAZON_SOCIAL}
                           variant="outlined"
                           margin="dense"
                           fullWidth
                           required
                           onChange={handleOnChange}
                           onBlur={onBlurValidacion}
                           value={campos[RAZON_SOCIAL].valor}
                           error={campos[RAZON_SOCIAL].error !== ''}
                           helperText={campos[RAZON_SOCIAL].error}
                        />
                     </Grid>
                  </Grid>
               </GroupBox>
            </Grid>
            <Grid container item xs={12}>
               <GroupBox label="Documento" fullWidth>
                  <Grid container spacing={1}>
                     <Grid item xs={3}>
                        <SelectDs
                           id="selectTipoDoc"
                           name={TIPO_DOC}
                           label="Tipo"
                           variant="outlined"
                           value={campos[TIPO_DOC].valor}
                           onChange={handleOnChange}
                           margin="dense"
                           data={tiposDocumento}
                           error={campos[TIPO_DOC].error !== ''}
                           helperText={campos[TIPO_DOC].error}
                           required
                           fullWidth
                        />
                     </Grid>
                     <Grid item xs={3}>
                        <TextField
                           label="Serie"
                           name={SERIE_DOC}
                           variant="outlined"
                           margin="dense"
                           fullWidth
                           required
                           onChange={handleOnChange}
                           onBlur={onBlurValidacion}
                           value={campos[SERIE_DOC].valor}
                           error={campos[SERIE_DOC].error !== ''}
                           helperText={campos[SERIE_DOC].error}
                        />
                     </Grid>
                     <Grid item xs={3}>
                        <TextField
                           label="Número"
                           name={NRO_DOC}
                           variant="outlined"
                           margin="dense"
                           fullWidth
                           required
                           onChange={handleOnChange}
                           onBlur={onBlurValidacion}
                           value={campos[NRO_DOC].valor}
                           error={campos[NRO_DOC].error !== ''}
                           helperText={campos[NRO_DOC].error}
                        />
                     </Grid>
                     <Grid item xs={3} style={{paddingTop: '16px'}}>
                        <DatePicker
                           name={FECHA_EMISION}
                           margin="dense"
                           label="Fecha Emision"
                           format="dd/MM/yyyy"
                           value={campos[FECHA_EMISION].valor}
                           onChange={date => {
                              validacionManual(FECHA_EMISION, date);
                           }}
                           inputVariant="outlined"
                           KeyboardButtonProps={{
                              'aria-label': 'elegir fecha'
                           }}
                           fullWidth
                           required
                        />
                     </Grid>
                  </Grid>
               </GroupBox>
            </Grid>
            <Grid container item xs={12}>
               <GroupBox label="Datos de la Suscripción">
                  <Grid container spacing={1} alignItems="center">
                     <Grid item xs={3}>
                        <SelectDs
                           id="selectPlan"
                           name={PLAN}
                           label="Plan"
                           variant="outlined"
                           value={campos[PLAN].valor}
                           onChange={e => {
                              handleOnPlanElegidoChange(e.target.value, planes);
                           }}
                           margin="dense"
                           data={planes}
                           error={campos[PLAN].error !== ''}
                           helperText={campos[PLAN].error}
                           genItemValue={item => {
                              return item.id;
                           }}
                           genItemKey={item => item.id}
                           genItemLabel={genLabel}
                           required
                           fullWidth
                        />
                     </Grid>
                     <Grid item xs={4}>
                        <SelectDs
                           id="selectProducto"
                           name={PRODUCTO}
                           label="Producto"
                           variant="outlined"
                           value={campos[PRODUCTO].valor}
                           onChange={e => {
                              if (e.currentTarget.dataset.item) {
                                 const item = JSON.parse(e.currentTarget.dataset.item);
                                 setCampo(PRODUCTO_DENOMINACION, item.denominacion);
                              }
                              setCampo(PRODUCTO, e.target.value);
                           }}
                           margin="dense"
                           data={productos}
                           error={campos[PRODUCTO].error !== ''}
                           helperText={campos[PRODUCTO].error}
                           genItemValue={genValue}
                           genItemKey={genValue}
                           genItemLabel={genLabel}
                           required
                           fullWidth
                        />
                     </Grid>
                     <Grid item xs={2}>
                        <TextField
                           label="Año"
                           name={ANIO}
                           variant="outlined"
                           margin="dense"
                           type="number"
                           fullWidth
                           required
                           onChange={handleOnChange}
                           onBlur={onBlurValidacion}
                           value={campos[ANIO].valor}
                           error={campos[ANIO].error !== ''}
                           helperText={campos[ANIO].error}
                           InputLabelProps={{
                              shrink: true
                           }}
                        />
                     </Grid>
                     <Grid item xs={3} style={{paddingTop: '12px'}}>
                        <DatePicker
                           name={FECHA_EXPIRACION}
                           margin="dense"
                           label="Fecha Expiración"
                           format="dd/MM/yyyy"
                           value={campos[FECHA_EXPIRACION].valor}
                           onChange={date => {
                              validacionManual(FECHA_EXPIRACION, date);
                           }}
                           inputVariant="outlined"
                           KeyboardButtonProps={{
                              'aria-label': 'elegir fecha'
                           }}
                           fullWidth
                           required
                        />
                     </Grid>
                     <Grid item xs={2}>
                        <TextField
                           label="Precio"
                           name={PRECIO}
                           variant="outlined"
                           margin="dense"
                           type="number"
                           fullWidth
                           onChange={handleOnChange}
                           value={campos[PRECIO].valor}
                           inputProps={{min: 1}}
                           InputLabelProps={{
                              shrink: true
                           }}
                        />
                     </Grid>
                     <Grid item xs={2}>
                        <TextField
                           label="Nro. Usuarios"
                           name={NRO_USUARIOS}
                           variant="outlined"
                           margin="dense"
                           type="number"
                           fullWidth
                           onChange={handleOnChange}
                           value={campos[NRO_USUARIOS].valor}
                           inputProps={{min: 1}}
                           InputLabelProps={{
                              shrink: true
                           }}
                        />
                     </Grid>
                     <Grid item xs={3}>
                        <TextField
                           label="Almacenamiento"
                           name={ALMACENAMIENTO}
                           variant="outlined"
                           margin="dense"
                           type="number"
                           fullWidth
                           onChange={handleOnChange}
                           value={campos[ALMACENAMIENTO].valor}
                           inputProps={{min: 1}}
                           InputLabelProps={{
                              shrink: true
                           }}
                        />
                     </Grid>
                     <Grid item xs={5}>
                        <TextField
                           label="Email"
                           name={EMAIL}
                           variant="outlined"
                           margin="dense"
                           fullWidth
                           onChange={handleOnChange}
                           onBlur={onBlurValidacion}
                           value={campos[EMAIL].valor}
                           error={campos[EMAIL].error !== ''}
                           helperText={campos[EMAIL].error}
                           required
                        />
                     </Grid>
                  </Grid>
               </GroupBox>
            </Grid>
            <Grid container item xs={12} spacing={1}>
               <Grid item xs={6}>
                  <Button
                     variant="contained"
                     onClick={inicializarFormulario}
                     fullWidth
                     disabled={existeError}
                  >
                     Cancelar
                  </Button>
               </Grid>
               <Grid item xs={6}>
                  <Button
                     variant="contained"
                     onClick={procesarRegistrarSuscripcion}
                     fullWidth
                     disabled={existeError || !validSuscription}
                     color="secondary"
                  >
                     Registrar Suscripcion
                  </Button>
               </Grid>
            </Grid>
         </Grid>
      </Container>
   );
}

export default RegistrarSuscripcionNube;
